import _ from 'lodash';
import DateUtilities from 'records/DateUtilities';
import { inputDateFormat, displayDateFormat, sortProps, defaultSortId } from 'constants/salesPeriods';
import ListUtilities from 'records/ListUtilities';
import { COLUMN_KEY, DIRECTION_KEY } from 'records/Sort';

/**
 * Process period list data. 
 * 1. sort data according to the given sort-id
 * 2. remove empty/null objects
 * 3. convert date formats to display formats
 * 
 * @param {List} salesPeriods list of raw sales-peiod objects
 * @param {Number} sortId sort-id according to sort-props
 * @returns {List} processed sales-period objects
 */
export const processPeriods = (salesPeriods, sortId) => {
    if (_.isEmpty(salesPeriods)) {
        return [];
    }
    let period;
    const result = [];

    const sortOption = sortProps[sortId];
    const sortedResult = ListUtilities.sortByColumn(salesPeriods, {
        [COLUMN_KEY]: sortOption.field,
        [DIRECTION_KEY]: sortOption.order,
    });

    // display data
    for (period of sortedResult) {
        if (_.isEmpty(period)) {
            continue;
        }
        result.push({
            periodId: period.periodId,
            periodName: period.periodName,
            startDate: DateUtilities.convertTimeFormat(period.startDate, inputDateFormat, displayDateFormat),
            endDate: DateUtilities.convertTimeFormat(period.endDate, inputDateFormat, displayDateFormat),
        })
    }
    return result;
}

/**
 * Decide whther to rerender on prop changes
 * @param {Object} prevProps previous salesPeriod store object
 * @param {Object} props updated salesPeriod store object
 * @param {List} displayedPeriods elements of internal componenet react state 
 */
export const shouldUpdateRender = (prevProps, props, rawInternalState) => {
    const processedProps = props || {};
    const { data: prevSalesPeriods, sortId: prevSortId } = prevProps || {};
    const { sortId } = processedProps;
    const salesPeriods = processedProps.data || [];
    const displayPeriods = rawInternalState || [];

    return prevSortId !== sortId || displayPeriods.length !== salesPeriods.length
        || !_.isEqual(prevSalesPeriods, salesPeriods)
}

/**
 * Get period name of the selected period
 * @param {Number} currentPeriodId selected periodId
 * @param {List} salesPeriods sales periods
 */
export const getPeriodName = (currentPeriodId, rawSalesPeriods) => {
    const salesPeriods = rawSalesPeriods || [];
    const selectedPeriod = salesPeriods.find(item => item.periodId === currentPeriodId) || {};
    return selectedPeriod.periodName || '';
}

/**
 * Remove null fields and create the patch body
 * @param {Object} original non-updated body eg:{periodName:'original',startDate:'2021-10-10',endDdate:'2021-12-12'}
 * @param {Object} param1 patch body eg:{periodName:'updated',startDate:'2021-10-10',endDdate:'2021-12-12'}
 * @returns processed patch body eg:{periodName:'updated'}
 */
export const createPatch = updated => {
    const updateBody = {}

    if (_.isEmpty(updated)) {
        return null;
    }

    const { periodName, startDate, endDate } = updated;

    updateBody.periodName = periodName;
    updateBody.startDate = startDate;
    updateBody.endDate = endDate;

    return updateBody;
}

/**
 * Derive the next suitable periodId after deleting
 * @param {Number} deletingId periodId of the deleting element
 * @param {Array} salesPeriods sales period data
 * @param {Number} sortId sales period data
 * @returns {Number} next suitable periodId
 */
export const getNextSelectedPeriodId = (deletingId, salesPeriods, sortId) => {
    if (!deletingId || !salesPeriods || salesPeriods.length === 0) {
        return null;
    }
    sortId = sortId || defaultSortId;
    const sortOption = sortProps[sortId];

    // derive currently sorted order of periodIds
    let periodIds = ListUtilities.sortByColumn(salesPeriods, {
        [COLUMN_KEY]: sortOption.field,
        [DIRECTION_KEY]: sortOption.order,
    }).map(element => element.periodId);
    // array index of the deleting period element
    let deletingIndex = periodIds.indexOf(deletingId);
    periodIds = periodIds.filter(item => item !== deletingId);
    if (!periodIds || periodIds.length === 0) {
        return null;
    }
    deletingIndex = Math.min(deletingIndex, salesPeriods.length - 2);
    return periodIds[deletingIndex];
}
